// Persistence for the new/old UI choice.
//
// Sugar is the master of the setting: it stores "new_ui" in the user preferences and
// reports it back through the iframe message. That answer arrives late - the iframe has
// to load first - and some legacy pages (mapping3.php and other standalone entry points)
// are rendered without FrontEndSugarConfig and never report it at all. So the value is
// resolved locally *before* the first render and only corrected if sugar disagrees;
// otherwise every page load would paint the old UI first and flip to the new one.
//
// Three stores are kept in sync:
//   cookie          - written on the registrable parent domain ("urakointiplus.com"),
//                     so the react app and sugar itself see the same value even on the
//                     very first visit to a subdomain that has never stored anything.
//   localStorage    - survives a new tab or a browser restart when cookies are blocked.
//   sessionStorage  - the legacy key, still read so users who toggled the UI before this
//                     change do not get bounced back.

const KEY = 'newUI';
const PARAM = 'new_ui';

const readLocalStorage = () => {
    try {
        return window.localStorage.getItem(KEY);
    } catch (e) {
        return null;
    }
}

const writeLocalStorage = (value) => {
    try {
        window.localStorage.setItem(KEY, value);
    } catch (e) {
        // ignore
    }
}

const readSessionStorage = () => {
    try {
        return window.sessionStorage.getItem(KEY);
    } catch (e) {
        return null;
    }
}

const writeSessionStorage = (value) => {
    try {
        window.sessionStorage.setItem(KEY, value);
    } catch (e) {
        // ignore
    }
}

const readCookie = () => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + KEY + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
}

// The registrable parent of the current host, so the cookie is shared between the react
// app (app.urakointiplus.com) and sugar (*.erp.urakointiplus.com). Empty for "localhost"
// and bare ip addresses - browsers reject a domain attribute for those.
const cookieDomain = () => {
    const host = window.location.hostname || '';
    if (host.indexOf('.') === -1 || /^[\d.]+$/.test(host)) {
        return '';
    }
    return '.' + host.split('.').slice(-2).join('.');
}

const writeCookie = (value) => {
    // One year, root path, so every route of the app sees the same value.
    const attributes = ';path=/;max-age=31536000;SameSite=Lax' +
        (window.location.protocol === 'https:' ? ';Secure' : '');
    const domain = cookieDomain();

    if (domain) {
        document.cookie = KEY + '=' + encodeURIComponent(value) + ';domain=' + domain + attributes;
        // A host only cookie left over from an earlier version would shadow the shared one
        // (same name, same path - readCookie() cannot tell them apart), so drop it.
        document.cookie = KEY + '=;path=/;max-age=0';
        if (readCookie() === value) {
            return;
        }
    }

    // Parent domain rejected (or no domain to use): keep a host only cookie instead.
    document.cookie = KEY + '=' + encodeURIComponent(value) + attributes;
}

const parse = (raw) => {
    if (raw === 'true' || raw === '1') {
        return true;
    }
    if (raw === 'false' || raw === '0') {
        return false;
    }
    return null;
}

// "new_ui" in the address bar wins: it is the value the user just toggled, on its way to
// sugar. Without this a refresh or a bookmarked link carrying it would render the other
// UI until the iframe reported back.
const readUrlParam = () => {
    try {
        return parse(new URLSearchParams(window.location.search).get(PARAM));
    } catch (e) {
        return null;
    }
}

/**
 * The last known new/old UI setting, or null when nothing has ever been stored.
 *
 * @returns {boolean|null}
 */
export const readStoredNewUI = () => {
    return parse(readCookie()) ?? parse(readLocalStorage()) ?? parse(readSessionStorage());
}

/**
 * The last known new/old UI setting. Used whenever sugar does not report which UI is in
 * use.
 *
 * @returns {boolean}
 */
export const readNewUI = () => readStoredNewUI() === true;

/**
 * The setting to render with before sugar has had a chance to report anything. Called
 * once, while the redux initial state is built.
 *
 * @returns {boolean}
 */
export const resolveInitialNewUI = () => {
    const fromUrl = readUrlParam();
    if (fromUrl !== null) {
        writeNewUI(fromUrl);
        return fromUrl;
    }
    return readNewUI();
}

/**
 * Remember the new/old UI setting.
 *
 * @param {boolean} value
 */
export const writeNewUI = (value) => {
    const stored = value ? 'true' : 'false';

    writeCookie(stored);
    writeLocalStorage(stored);
    // Kept in sync for anything still reading the old sessionStorage key.
    writeSessionStorage(stored);
}
